import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Filter } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import CartSidebar from '../components/CartSidebar';
import PromoCodeInput from '../components/PromoCodeInput';
import { fetchProducts } from '../data/products';
import { useCartStore } from '../store/cartStore';
import { trackPageView } from '../lib/analytics';
import { formatPrice } from '../lib/utils';
import type { Product } from '../types';
import { SITE_NAME } from '../lib/brand';

const BRAND_GROUP_OPTIONS = [
  { slug: 'hk_brand', name: '香港品牌' },
  { slug: 'overseas_brand', name: '海外品牌' },
];

// Restaurant offers moved to the dedicated /offers page — never show them in the store.
const STORE_EXCLUDED_BRAND_GROUPS = ['local_restaurant', 'mainland_restaurant'];

const CATEGORY_LABELS: Record<string, string> = {
  spice: '辣粉',
  chili_oil: '辣油',
  product: '產品',
};

// Seeded PRNG so the random order stays stable during the current page session.
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const DAY_SECONDS = 86400;

function getProductWeight(createdAt?: number): number {
  if (!createdAt) return 1;
  const ageDays = (Math.floor(Date.now() / 1000) - createdAt) / DAY_SECONDS;
  if (ageDays <= 7) return 5;
  if (ageDays <= 30) return 2;
  return 1;
}

function weightedShuffle<T>(items: T[], weightFn: (item: T) => number, seed: number): T[] {
  const rng = mulberry32(seed);
  const scored = items.map((item) => ({
    item,
    score: -Math.log(Math.max(rng(), 1e-10)) / weightFn(item),
  }));
  scored.sort((a, b) => a.score - b.score);
  return scored.map((s) => s.item);
}

export default function Store() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [brandGroupFilter, setBrandGroupFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [cartOpen, setCartOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [shuffleSeed, setShuffleSeed] = useState(() => Date.now());
  const ITEMS_PER_PAGE = 20;

  const totalItems = useCartStore((s) => s.totalItems());
  const subtotal = useCartStore((s) => s.subtotal());

  useEffect(() => {
    trackPageView('/store');
    // Products arrive already brandified («X-1493») from the fetchProducts
    // data layer — no per-page transform needed here.
    fetchProducts().then((data) => {
      setProducts(data);
      setLoading(false);
    });
  }, []);

  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      if (brandGroupFilter === 'all' || p.brandGroup === brandGroupFilter) {
        if (p.brand) set.add(p.brand);
      }
    });
    return Array.from(set).sort();
  }, [products, brandGroupFilter]);

  const categoryOptions = useMemo(() => {
    const map = new Map<string, string>();
    products.forEach((p) => {
      if (p.category) map.set(p.category, CATEGORY_LABELS[p.category] || p.category);
    });
    return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
  }, [products]);

  const noFiltersActive =
    brandGroupFilter === 'all' && brandFilter === 'all' && categoryFilter === 'all';

  const sortedProducts = useMemo(() => {
    if (noFiltersActive) {
      return weightedShuffle(products, (p) => getProductWeight(p.createdAt), shuffleSeed);
    }
    return [...products].sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id));
  }, [products, noFiltersActive, shuffleSeed]);

  const filteredProducts = sortedProducts.filter((p) => {
    if (STORE_EXCLUDED_BRAND_GROUPS.includes(p.brandGroup || '')) return false;
    const groupMatch = brandGroupFilter === 'all' || p.brandGroup === brandGroupFilter;
    const brandMatch = brandFilter === 'all' || p.brand === brandFilter;
    const categoryMatch = categoryFilter === 'all' || p.category === categoryFilter;
    return groupMatch && brandMatch && categoryMatch;
  });

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [brandGroupFilter, brandFilter, categoryFilter, shuffleSeed]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} />

      <section className="relative bg-x1493-dark text-white py-12 md:py-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/media/media/1786030012_hkgspicyvalley_bg.jpeg')" }}
        />
        <div className="absolute inset-0 bg-x1493-dark/70" />
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{SITE_NAME} 商店</h1>
          <p className="text-white/80">選購你喜愛的港式辣粉，一鍵加入購物車</p>
        </div>
      </section>

      <section className="py-8 sticky top-[72px] z-30 bg-white/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row gap-4 justify-between items-center">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={18} className="text-stone-500" />
            <select
              value={brandGroupFilter}
              onChange={(e) => {
                setBrandGroupFilter(e.target.value);
                setBrandFilter('all');
              }}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部品牌群組</option>
              {BRAND_GROUP_OPTIONS.map((b) => (
                <option key={b.slug} value={b.slug}>{b.name}</option>
              ))}
            </select>
            <select
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部品牌</option>
              {brandOptions.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="p-2 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-x1493-primary outline-none"
            >
              <option value="all">全部類別</option>
              {categoryOptions.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            {noFiltersActive && (
              <button
                onClick={() => setShuffleSeed(Date.now())}
                className="flex items-center gap-1.5 text-sm font-bold text-x1493-primary border border-x1493-primary px-3 py-2 rounded-xl hover:bg-x1493-primary hover:text-white transition"
                title="隨機洗牌，新產品權重更高"
              >
                <span>辣手抽籤</span>
                <span>🎲</span>
              </button>
            )}
          </div>

          <button
            onClick={() => setCartOpen(true)}
            className="flex items-center gap-2 bg-x1493-primary text-white px-5 py-2.5 rounded-xl font-bold hover:bg-red-700 transition"
          >
            <ShoppingCart size={20} />
            <span>購物車</span>
            {totalItems > 0 && (
              <span className="bg-white text-x1493-primary text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
            <span className="hidden sm:inline">· {formatPrice(subtotal)}</span>
          </button>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {loading ? (
                <div className="text-center py-20">載入中...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {paginatedProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40 hover:bg-stone-50"
                  >
                    上一頁
                  </button>
                  <span className="text-sm text-stone-600">
                    第 {currentPage} / {totalPages} 頁（共 {filteredProducts.length} 件）
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg border border-stone-200 text-sm disabled:opacity-40 hover:bg-stone-50"
                  >
                    下一頁
                  </button>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-stone-50 rounded-2xl p-6 sticky top-40">
                <h3 className="font-bold text-lg mb-4">購物車摘要</h3>
                {totalItems === 0 ? (
                  <p className="text-stone-500 text-sm mb-4">購物車尚無商品</p>
                ) : (
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-600">件數</span>
                      <span className="font-semibold">{totalItems}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>小計</span>
                      <span className="text-x1493-primary">{formatPrice(subtotal)}</span>
                    </div>
                  </div>
                )}

                <PromoCodeInput orderAmount={subtotal} />

                <Link
                  to="/checkout"
                  className={`block w-full text-center mt-6 py-3 rounded-xl font-bold transition ${
                    totalItems > 0
                      ? 'bg-x1493-primary text-white hover:bg-red-700'
                      : 'bg-stone-200 text-stone-400 cursor-not-allowed pointer-events-none'
                  }`}
                >
                  前往結帳
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
